/**
 * Insights da aula — a "visão de estúdio". Os números vêm prontos da API
 * (`/progress/lessons/{id}/insights`); aqui só a linguagem de exibição.
 */
import type { components } from '@/core/http';

export type LessonInsights = components['schemas']['LessonInsightsOut'];

export const PERIODS = [
  { days: 7, label: 'Últimos 7 dias' },
  { days: 28, label: 'Últimos 28 dias' },
  { days: 90, label: 'Últimos 90 dias' },
  { days: 365, label: 'Último ano' },
  { days: 0, label: 'Desde sempre' },
] as const;
export type PeriodDays = (typeof PERIODS)[number]['days'];

export function isPeriod(v: unknown): v is PeriodDays {
  return PERIODS.some((p) => p.days === v);
}

/** Variação contra o período anterior, em %. `null` quando não há base. */
export function deltaPercent(current: number, previous: number | undefined | null): number | null {
  if (previous === undefined || previous === null || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

/** "1:05", "12:40", "1:02:03" — posição no vídeo. */
export function clock(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** Tempo de exibição: "45 s", "12 min", "3,4 h". */
export function watchTime(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  return `${(seconds / 3600).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h`;
}

const dayFmt = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const longDayFmt = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/** "5 de out." a partir de "2026-10-05" — a data já vem no fuso de Brasília. */
export const dayLabel = (iso: string) => dayFmt.format(new Date(`${iso}T00:00:00Z`)).replace(' de ', ' ');
export const longDayLabel = (iso: string) => longDayFmt.format(new Date(`${iso}T00:00:00Z`));

/** Ordem de `datetime.weekday()` no back: segunda = 0. */
export const WEEKDAYS = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'] as const;

/** A faixa de horário com mais visualizações: "20h–21h". */
export function peakHour(hours: readonly number[]): string | null {
  const max = Math.max(...hours);
  if (!(max > 0)) return null;
  const h = hours.indexOf(max);
  return `${h}h–${(h + 1) % 24}h`;
}
